import React from 'react';
import { FileText, CalendarCheck, FlaskConical, CreditCard, AlertTriangle, Scale } from 'lucide-react';

const sections = [
  {
    id: "acceptance",
    icon: <FileText className="w-5 h-5 text-blue-600" />,
    title: "قبول الشروط",
    color: "bg-blue-50",
    body: [
      "يُعد استخدامك لموقعنا أو حجز أي خدمة من خدماتنا موافقة صريحة منك على شروط الاستخدام الموضحة هنا.",
      "إذا كنت لا توافق على أي بند من هذه الشروط، يرجى التوقف عن استخدام الموقع أو الخدمة.",
      "نحتفظ بحق تعديل هذه الشروط في أي وقت، وسيتم نشر أي تحديث على هذه الصفحة."
    ]
  },
  {
    id: "appointments",
    icon: <CalendarCheck className="w-5 h-5 text-teal-600" />,
    title: "المواعيد والحجوزات",
    color: "bg-teal-50",
    body: [
      "الحجز عبر الموقع أو الهاتف يعتبر طلبًا أوليًا لموعد، وقد يتطلب تأكيدًا من فريق خدمة العملاء.",
      "يرجى الحضور في الموعد المحدد أو التواصل معنا مسبقًا في حال الرغبة بإلغاء أو تعديل الموعد.",
      "نحتفظ بحق إعادة جدولة أي موعد لأسباب تشغيلية مع إخطارك بذلك في أقرب وقت ممكن."
    ]
  },
  {
    id: "services",
    icon: <FlaskConical className="w-5 h-5 text-purple-600" />,
    title: "طبيعة الخدمات",
    color: "bg-purple-50",
    body: [
      "تقدَّم نتائج التحاليل لأغراض معلوماتية وتشخيصية، ولا تُغني عن استشارة الطبيب المختص.",
      "نبذل أقصى درجات الدقة والجودة في إجراء التحاليل، وفق المعايير الطبية المعتمدة.",
      "في حال الاشتباه بخطأ في نتيجة تحليل، يحق لك طلب إعادة الفحص وفقًا لسياسة المعمل الداخلية."
    ]
  },
  {
    id: "payments",
    icon: <CreditCard className="w-5 h-5 text-amber-600" />,
    title: "الأسعار والدفع",
    color: "bg-amber-50",
    body: [
      "تُعرض أسعار الخدمات في المعمل أو عبر ممثلي خدمة العملاء، وقد تخضع للتغيير دون إشعار مسبق.",
      "يتم الدفع في الغالب عند تقديم الخدمة، ما لم يُتفق على خلاف ذلك.",
      "الفواتير والإيصالات متاحة عند الطلب لجميع الخدمات المقدمة."
    ]
  },
  {
    id: "liability",
    icon: <AlertTriangle className="w-5 h-5 text-rose-600" />,
    title: "حدود المسؤولية",
    color: "bg-rose-50",
    body: [
      "لا يتحمل المعمل مسؤولية أي قرار طبي يُتخذ بناءً على نتيجة تحليل دون الرجوع إلى الطبيب المعالج.",
      "لا نتحمل مسؤولية أي تأخير ناتج عن ظروف خارجة عن إرادتنا، مثل الأعطال التقنية أو الظروف الطارئة.",
      "يقتصر التزامنا على تقديم الخدمة بالمعايير المهنية المتعارف عليها في هذا المجال."
    ]
  }
];

const TermsOfUse = () => {
  return (
    <section id="terms-of-use" className="py-24 bg-slate-50 font-sans" dir="rtl">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-1.5 mb-5">
            <Scale className="w-4 h-4" />
            شروط واضحة وعادلة
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-5 tracking-tight">
            شروط الاستخدام
          </h2>
          <div className="w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            توضح هذه الصفحة الشروط والأحكام الخاصة باستخدام موقع وخدمات معمل النخبة، يرجى قراءتها بعناية قبل استخدام خدماتنا
          </p>
          <p className="text-sm text-gray-400 mt-4">آخر تحديث: يوليو 2026</p>
        </div>

        <div className="space-y-6">
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className={`${section.color} p-6 rounded-2xl shadow-sm border border-gray-100`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm shrink-0">
                  {section.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.body.map((line, i) => (
                      <li key={i} className="text-gray-600 leading-relaxed flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0"></span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white shadow-lg rounded-2xl p-8 border border-gray-100 text-center">
          <h3 className="text-xl font-bold text-blue-900 mb-2">هل لديك استفسار حول هذه الشروط؟</h3>
          <p className="text-gray-600 mb-1">
            يسعدنا الرد على أي سؤال يخص شروط الاستخدام عبر البريد الإلكتروني
          </p>
          <p className="text-blue-700 font-semibold">support@medicallab.com</p>
        </div>
      </div>
    </section>
  );
};

export default TermsOfUse;
